import { useCallback, useState } from 'react'
import type {
  CreateSenderPayload,
  Sender,
  SenderAddressDefaultsResponse,
  SenderMethod,
  UpdateSenderPayload,
} from 'types/senders'
import type { MutationResult, QueryResult } from 'utils/api'

import { useApiQuery } from '../useApiQuery'
import { useFetchApi } from '../useFetchApi'

const unwrapSenders = (response: { data: Sender[] }) => response.data

export function useSenders(method: SenderMethod = 'EMAIL'): QueryResult<Sender[]> {
  return useApiQuery(method === 'SMS' ? '/api/senders?method=SMS' : '/api/senders', { select: unwrapSenders })
}

export function useSender(id: string | undefined): QueryResult<Sender> {
  return useApiQuery<Sender>(id ? `/api/senders/${encodeURIComponent(id)}` : null)
}

export function useSenderAddressDefaults(): QueryResult<SenderAddressDefaultsResponse> {
  return useApiQuery<SenderAddressDefaultsResponse>('/api/senders/address-defaults')
}

export function useCreateSender(): MutationResult<Sender, [CreateSenderPayload]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (payload: CreateSenderPayload): Promise<Sender> => {
      setLoading(true)
      try {
        return await fetchApi<Sender>('/api/senders', {
          method: 'POST',
          body: JSON.stringify(payload),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useUpdateSender(): MutationResult<Sender, [string, UpdateSenderPayload]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (id: string, payload: UpdateSenderPayload): Promise<Sender> => {
      setLoading(true)
      try {
        return await fetchApi<Sender>(`/api/senders/${encodeURIComponent(id)}`, {
          method: 'PATCH',
          body: JSON.stringify(payload),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useDeleteSender(): MutationResult<void, [string]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (id: string): Promise<void> => {
      setLoading(true)
      try {
        await fetchApi<{ ok: true }>(`/api/senders/${encodeURIComponent(id)}`, {
          method: 'DELETE',
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useResendSenderVerification(): MutationResult<void, [string]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (id: string): Promise<void> => {
      setLoading(true)
      try {
        await fetchApi<{ ok: true }>(`/api/senders/${encodeURIComponent(id)}/resend-verification`, {
          method: 'POST',
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}
